import { z } from 'zod'
import { reviewParamsSchema } from './data'
import { widgetSchema } from '../../common/schema'

export const reviewsWidgetConfig = widgetSchema.extend({
  section: z.object({
    show: z.boolean().default(false).describe('Show the section title above the widget content'),
    title: z.string().nullish().default('Reviews').describe('Section title text'),
  }).default({ show: false, title: 'Reviews' }).describe('Section heading shown above the widget content'),
  layout: z.enum(['carousel', 'grid', 'badge', 'list']).default('badge').describe('The layout of the widget'),
  filter: reviewParamsSchema,
  header: z.object({
    show: z.boolean().default(true).describe('Show the header section (title + average rating)'),
    title: z.string().default('What Our Customers Say').describe('The title of the header'),
    buttonText: z.string().default('Review us').describe('The text of the button'),
    buttonHref: z.string().optional().describe('The href of the button'),
  }).optional().describe('The header of the widget'),
  hideName: z.boolean().optional().describe('Whether to hide the name of the reviewer'),
  hideProperty: z.boolean().optional().describe('Whether to hide the property name'),
  linkToProperty: z.boolean().default(false).describe('Link each review to its property page: the property name becomes the link (the review title when the property name is hidden; list rows gain a "Stayed at" line). Uses the feed\'s property page route; a review whose property has no slug stays plain text'),
  hideRating: z.boolean().optional().describe('Whether to hide the rating'),
  hideFeedback: z.boolean().optional().describe('Whether to hide the feedback'),
  hideDate: z.boolean().optional().describe('Whether to hide the date'),
  hideSource: z.boolean().optional().describe('Whether to hide the review source badge'),
  hideOverallRating: z.boolean().optional().describe('Whether to hide the overall rating display'),
  maxFeedbackLength: z.number().default(250).describe('Maximum feedback characters shown on carousel, grid, and badge review cards (the list layout always shows full feedback). Set to 0 to disable truncation'),
  showSearch: z.boolean().default(false).describe('Show a keyword search box that filters the loaded reviews by review text, guest name, and property name (carousel, grid, and list layouts; the badge layout has no search box)'),
  popupForDetail: z.boolean().default(true).describe('Open the full review in a popup when its feedback is truncated'),
  carousel: z.object({
    hideArrows: z.boolean().optional().describe('Whether to hide the arrows'),
    hideDots: z.boolean().optional().describe('Whether to hide the dots'),
  }).optional().describe('The carousel options'),
  grid: z.object({
    type: z.enum(['general', 'masonry']).default('general').describe('The type of the grid'),
    columnCount: z.number().min(1).max(6).default(1).describe('Columns at 480px mobile first'),
    columnCount780: z.number().min(1).max(6).default(2).describe('Columns at 780px'),
    columnCount1200: z.number().min(1).max(6).default(4).describe('Columns at 1200px'),
  }).optional().describe('The grid options'),
  badge: z.object({
    type: z.enum(['static', 'floating']).default('floating').describe('The type of the badge'),
    position: z.enum(['top-left', 'top-right', 'bottom-left', 'bottom-right']).default('bottom-right').describe('The position of the badge'),
    modalTitle: z.string().default('Reviews').describe('Heading shown inside the reviews panel the badge opens'),
  }).optional().describe('The badge options'),
  list: z.object({
    initialCount: z.number().int().min(1).max(50).default(3).describe('How many reviews to show before "Show More"'),
    showTitle: z.boolean().default(true).describe('Show the review title when the review has its own title (titles that just repeat the start of the content are never shown)'),
    showDividers: z.boolean().default(true).describe('Show horizontal dividers between reviews'),
    showMoreText: z.string().default('Show More').describe('Label for the show more button'),
    showMoreAlignment: z.enum(['left', 'center', 'right']).default('center').describe('Horizontal alignment of the Show More button'),
    modalTitle: z.string().default('All Reviews').describe('Title shown in the all-reviews modal'),
  }).optional().describe('The list layout options'),
})

export type ReviewsWidgetConfig = z.infer<typeof reviewsWidgetConfig>
